/**
 * ADR-0185 §3.5 — "Who's out / What's down": the disruptions face. Add an
 * operator absence, a machine-down / tooling-out window, or a job pin; the
 * active list carries attribution (who, when — absolute) and Clear. The kinds
 * and their subject shapes come from the served disruptions card — nothing is
 * restated here. In scenario mode the same panel collects HYPOTHETICAL entries
 * that live only in the what-if frame.
 */
import { useState } from "react";
import { formatTimestamp } from "../../../../lib/formatTime.js";
import type { AcceptedDisruption, BoardOverlay, ScenarioEntry } from "../../boardTypes.js";

function subjectText(kind: string, subject: Record<string, unknown>): string {
  const s = (k: string) => (typeof subject[k] === "string" ? String(subject[k]) : "");
  switch (kind) {
    case "operator_absence":
      return s("operator_label");
    case "machine_unavailable":
      return s("machine");
    case "tooling_unavailable":
      return s("tooling_note") ? `${s("machine")} — ${s("tooling_note")}` : s("machine");
    case "job_pin":
      return `job ${s("job")}`;
    default:
      return Object.values(subject).filter((v) => typeof v === "string").join(", ");
  }
}

function windowText(startsAt: string | null, endsAt: string | null): string {
  const from = startsAt ? formatTimestamp(startsAt) : "—";
  return endsAt ? `${from} → ${formatTimestamp(endsAt)}` : `${from} → until cleared`;
}

export function DisruptionsPanel(props: {
  accepts: AcceptedDisruption[];
  overlays: BoardOverlay[];
  machines: string[];
  planJobs: Array<{ job: string; part: string | null }>;
  busy: boolean;
  mode: "live" | "scenario";
  scenarioEntries?: ScenarioEntry[];
  onAssert: (entry: ScenarioEntry & { note: string | null }) => void;
  onClear: (overlayId: string) => void;
  onRemoveScenarioEntry?: (index: number) => void;
}) {
  const [kind, setKind] = useState("");
  const [subject, setSubject] = useState<Record<string, string>>({});
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [note, setNote] = useState("");

  const accepted = props.accepts.find((a) => a.kind === kind) ?? null;
  const subjectComplete = accepted
    ? accepted.subject_required.every((k) => (subject[k] ?? "").trim() !== "")
    : false;

  function subjectField(key: string, required: boolean) {
    const label = key.replace(/_/g, " ");
    if (key === "machine") {
      return (
        <label className="field" key={key}>
          <span className="label">Machine{required ? "" : " (optional)"}</span>
          <select className="select" value={subject[key] ?? ""} onChange={(e) => setSubject({ ...subject, [key]: e.target.value })}>
            <option value="">Choose…</option>
            {props.machines.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>
      );
    }
    if (key === "job") {
      return (
        <label className="field" key={key}>
          <span className="label">Job{required ? "" : " (optional)"}</span>
          <select className="select" value={subject[key] ?? ""} onChange={(e) => setSubject({ ...subject, [key]: e.target.value })}>
            <option value="">Choose…</option>
            {props.planJobs.map((j) => (
              <option key={j.job} value={j.job}>
                {j.job}{j.part ? ` · ${j.part}` : ""}
              </option>
            ))}
          </select>
        </label>
      );
    }
    return (
      <label className="field" key={key}>
        <span className="label">{key === "operator_label" ? "Who" : label}{required ? "" : " (optional)"}</span>
        <input
          className="input"
          value={subject[key] ?? ""}
          onChange={(e) => setSubject({ ...subject, [key]: e.target.value })}
        />
      </label>
    );
  }

  function add() {
    if (!accepted || !startsAt || !subjectComplete) return;
    const cleanSubject: Record<string, unknown> = {};
    for (const k of [...accepted.subject_required, ...accepted.subject_optional]) {
      const v = (subject[k] ?? "").trim();
      if (v) cleanSubject[k] = v;
    }
    props.onAssert({
      kind: accepted.kind,
      subject: cleanSubject,
      starts_at: new Date(startsAt).toISOString(),
      ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      note: note.trim() || null,
    });
    setSubject({});
    setStartsAt("");
    setEndsAt("");
    setNote("");
  }

  const active = props.overlays.filter((o) => !o.cleared_at);

  return (
    <div className="boardPanel">
      <div style={{ display: "grid", gap: 10 }}>
        <label className="field">
          <span className="label">What happened?</span>
          <select
            className="select"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setSubject({});
            }}
          >
            <option value="">Choose…</option>
            {props.accepts.map((a) => (
              <option key={a.kind} value={a.kind}>{a.label}</option>
            ))}
          </select>
          {accepted ? <div className="help">{accepted.description}</div> : null}
        </label>
        {accepted ? (
          <>
            {accepted.subject_required.map((k) => subjectField(k, true))}
            {accepted.subject_optional.map((k) => subjectField(k, false))}
            <div className="inputRow">
              <label className="field" style={{ flex: 1 }}>
                <span className="label">From</span>
                <input className="input" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
              </label>
              <label className="field" style={{ flex: 1 }}>
                <span className="label">Until (blank = until cleared)</span>
                <input className="input" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
              </label>
            </div>
            {props.mode === "live" ? (
              <label className="field">
                <span className="label">Note (optional)</span>
                <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
              </label>
            ) : null}
            <div className="actions">
              <button className="btn btnSm" onClick={add} disabled={props.busy || !startsAt || !subjectComplete}>
                {props.mode === "scenario" ? "Add to scenario" : "Add"}
              </button>
            </div>
          </>
        ) : null}
      </div>

      {props.mode === "scenario" && (props.scenarioEntries?.length ?? 0) > 0 ? (
        <div style={{ marginTop: 10 }}>
          <div className="label">In this scenario</div>
          <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
            {props.scenarioEntries!.map((e, i) => (
              <div key={i} className="boardPanelRow">
                <span>
                  <strong>{props.accepts.find((a) => a.kind === e.kind)?.label ?? e.kind}</strong>{" "}
                  {subjectText(e.kind, e.subject)}
                  <span className="muted"> · {windowText(e.starts_at, e.ends_at)}</span>
                </span>
                {props.onRemoveScenarioEntry ? (
                  <button className="adminListAction" onClick={() => props.onRemoveScenarioEntry!(i)}>Remove</button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {props.mode === "live" ? (
        <div style={{ marginTop: 10 }}>
          <div className="label">Active</div>
          {active.length === 0 ? (
            <div className="muted" style={{ marginTop: 6 }}>Nothing marked — everyone in, everything up.</div>
          ) : (
            <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
              {active.map((o) => (
                <div key={o.id} className="boardPanelRow">
                  <span>
                    <strong>{props.accepts.find((a) => a.kind === o.kind)?.label ?? o.kind.replace(/_/g, " ")}</strong>{" "}
                    {subjectText(o.kind, o.subject)}
                    {o.note ? <span className="muted"> — {o.note}</span> : null}
                    <span className="muted"> · {windowText(o.starts_at, o.ends_at)}</span>
                    <span className="muted">
                      {" "}· by {o.created_by_name ?? "—"}, {formatTimestamp(o.created_at)}
                    </span>
                  </span>
                  <button className="adminListAction" onClick={() => props.onClear(o.id)} disabled={props.busy}>
                    Clear
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
